import React, { FC, useState } from 'react';
import { Article as IArticle } from 't2-graphql-types';
import { ArticleParentInfo } from './ArticleParentInfo';
import { ArticleParentPromptInfo } from './ArticleParentPromptInfo';
import { ArticleParentQuoteInfo } from './ArticleParentQuoteInfo';
import { ArticleType } from '../Article/types';
import { TCloudinaryImageShape } from '../CloudinaryResponsiveImage/types';
import getUserDisplayName from '../../../utils/getUserDisplayName';
import { useT2Query } from '../../hooks/useT2Query';
import { getQuoteParentArticleDataQuery } from '../Article/queries/getQuoteParentArticleDataQuery';

type TArticleParentInfoContainerProps = {
  parentArticle: IArticle;
  expandAll?: boolean;
  // mock prop used for rendering ArticleParentQuoteInfo without the quote type
  displayAsQuote?: boolean;
};

export const ArticleParentInfoContainer: FC<TArticleParentInfoContainerProps> = ({
  parentArticle,
  expandAll = false,
  displayAsQuote = false,
}) => {
  const [isOpen, setIsOpen] = useState(expandAll);
  const isPrompt = (parentArticle?.type as string) === ArticleType.Prompt;
  const isQuote = (parentArticle?.type as string) === ArticleType.Quote;

  const { data } = useT2Query(getQuoteParentArticleDataQuery, {
    variables: {
      parentArticleId: parentArticle?.id,
    },
    skip: !isQuote,
  });

  const quoteParentData = data?.article?.parentArticle;
  const articleParentInfoHeader = isQuote || displayAsQuote ? parentArticle?.shortDescription : parentArticle?.title;
  const quotationMarks = isQuote || displayAsQuote;

  const onClick = () => {
    setIsOpen(!isOpen);
  };

  const renderQuoteInfo = (article: IArticle, quoteData: IArticle) => (
    <ArticleParentQuoteInfo
      parentTitle={quoteData?.title ?? (article.title as string)}
      shortDescription={article.shortDescription as string}
      slug={quoteData?.slug ?? (article.slug as string)}
      parentAuthor={getUserDisplayName({
        name: quoteData?.author?.name ?? article.author?.name,
        username: quoteData?.author?.username ?? article.author?.username,
      })}
      image={(quoteData?.coverImage as TCloudinaryImageShape) ?? (article.coverImage as TCloudinaryImageShape)}
      deactivated={quoteData?.deactivated as boolean}
    />
  );

  return (
    <ArticleParentInfo
      title={articleParentInfoHeader as string}
      onClick={onClick}
      isOpen={isOpen}
      quotationMarks={quotationMarks}
    >
      {isPrompt && (
        <ArticleParentPromptInfo
          title={parentArticle?.title as string}
          shortDescription={parentArticle?.shortDescription as string}
          slug={parentArticle?.slug as string}
        />
      )}
      {(isQuote || displayAsQuote) && renderQuoteInfo(parentArticle, quoteParentData)}
    </ArticleParentInfo>
  );
};
